import React, { useState } from 'react';
import { Box, IconButton, Menu, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BrandedListingCard from '../BrandedShareChrome/BrandedListingCard';
import ActionMenuItem, { ACTION_MENU_LIST_PROPS, ACTION_MENU_PAPER_PROPS } from '../ActionMenuItem';
import RecordCommercialLine from '../../../Leads/components/RecordCommercialLine';
import { listCardActionIconSx } from '../UserLotListsPanel/UserLotListsPanel.styles';
import { recordCardHeaderIconSx } from '../RecordLotIdentity';

/**
 * Card de un registro (lote, UF o lote CAD) en Registros y en el detalle de
 * lista: la card del share (`BrandedListingCard`, `variant="record"`) con el ⋮
 * de acciones en el header y la línea de leads / consultas al pie.
 *
 * @param {{
 *   item: object,
 *   layout?: 'list'|'grid',
 *   isLocated?: boolean,
 *   actions?: Array<{ key: string, label: string, icon: React.ReactNode, onClick: Function, disabled?: boolean, loading?: boolean }>,
 *   onOpen?: Function,
 *   onHover?: Function,
 *   boundary?: Array|null,
 *   footerExtra?: React.ReactNode,
 *   reserveFooterExtra?: boolean,
 * }} props
 * `reserveFooterExtra`: la lista tiene fila extra al pie (chips de lista) y todas
 * las cards reservan ese alto, la tengan o no (alto fijo parejo).
 */
export default function RecordListingCard({
  item,
  layout = 'list',
  isLocated = false,
  actions = [],
  onOpen,
  onHover,
  isHighlighted = false,
  boundary = null,
  footerExtra = null,
  reserveFooterExtra = false,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const closeMenu = () => setMenuAnchor(null);
  const summary = item.commercial_summary;

  const menu = actions.length > 0 ? (
    <Box onClick={(event) => event.stopPropagation()} sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title="Más acciones" placement="top" arrow>
        <IconButton
          size="small"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          sx={{ ...listCardActionIconSx, ...recordCardHeaderIconSx }}
          aria-label="Más acciones"
        >
          <MoreVertIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={ACTION_MENU_PAPER_PROPS}
        MenuListProps={ACTION_MENU_LIST_PROPS}
      >
        {actions.map((action) => (
          <ActionMenuItem
            key={action.key}
            icon={action.icon}
            label={action.label}
            disabled={action.disabled}
            loading={action.loading}
            onClick={() => {
              closeMenu();
              // Diferir: el Menu suelta el foco antes de abrir un diálogo (aria-hidden).
              window.setTimeout(() => action.onClick?.(item), 0);
            }}
          />
        ))}
      </Menu>
    </Box>
  ) : null;

  const hasCommercial = summary && typeof summary === 'object';
  const footer = hasCommercial || footerExtra ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
      {hasCommercial ? (
        <RecordCommercialLine summary={summary} isCad={item.commercialIsCad} showZero />
      ) : null}
      {footerExtra}
    </Box>
  ) : null;

  return (
    <BrandedListingCard
      item={item}
      variant="record"
      layout={layout}
      isActive={isLocated}
      isHighlighted={isHighlighted}
      boundary={boundary}
      onOpen={onOpen}
      onHover={onHover}
      menu={menu}
      footer={footer}
      testId="record-listing-card"
      reserveFooterExtra={reserveFooterExtra}
    />
  );
}
